// Scheduling Dashboard - Main management interface for cal.larryshelp.org
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getSchedulingDashboard,
  getEventTypes,
  createEventType,
  deleteEventType,
  getCalendarStatus
} from '../../services/scheduling-api';
import { EventType, Booking, SchedulingStats, CalendarConnection } from '../../types/scheduling';
import SchedulingSetup from './SchedulingSetup';
import PaymentSettings from './PaymentSettings';
import APIKeys from './APIKeys';
import StaffManagement from './StaffManagement';
import SchedulingCalendarView from './SchedulingCalendarView';

type TabType = 'dashboard' | 'payments' | 'api' | 'staff' | 'calendar';

const SchedulingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isStaffSomewhere, setIsStaffSomewhere] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [profile, setProfile] = useState<{ subdomain: string; plan: string; bookingUrl: string } | null>(null);
  const [stats, setStats] = useState<SchedulingStats | null>(null);
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [calendarStatus, setCalendarStatus] = useState<{ google: CalendarConnection; microsoft: CalendarConnection } | null>(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDuration, setNewEventDuration] = useState(30);

  useEffect(() => {
    loadDashboard();
  }, []);

  const checkStaffMembership = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;
      const resp = await fetch('/backend/scheduling/staff/my-dashboard', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      return data.memberships && data.memberships.length > 0;
    } catch {
      return false;
    }
  };

  const loadDashboard = async () => {
    try {
      const dashboardData = await getSchedulingDashboard();

      if (dashboardData.needsSetup) {
        // Not an owner — check if they're staff somewhere
        const isStaff = await checkStaffMembership();
        if (isStaff) {
          // Staff-only user: send them to the staff dashboard
          navigate('/scheduling/staff', { replace: true });
          return;
        }
        setNeedsSetup(true);
        setLoading(false);
        return;
      }

      setProfile(dashboardData.profile);
      setStats(dashboardData.stats);
      setUpcomingBookings(dashboardData.upcomingBookings);

      const [eventTypesData, calStatus, isStaff] = await Promise.all([
        getEventTypes(),
        getCalendarStatus(),
        checkStaffMembership()
      ]);

      setEventTypes(eventTypesData.eventTypes);
      setCalendarStatus(calStatus);
      setIsStaffSomewhere(isStaff);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setNeedsSetup(false);
    loadDashboard();
  };

  const handleCreateEventType = async () => {
    if (!newEventTitle.trim()) return;

    try {
      await createEventType({
        title: newEventTitle,
        durationMinutes: newEventDuration
      });

      setNewEventTitle('');
      setNewEventDuration(30);
      setShowNewEventModal(false);

      const data = await getEventTypes();
      setEventTypes(data.eventTypes);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to create event type');
    }
  };

  const handleDeleteEventType = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;

    try {
      await deleteEventType(id);
      setEventTypes(eventTypes.filter(et => et.id !== id));
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete event type');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading scheduling dashboard...</p>
      </div>
    );
  }

  if (needsSetup) {
    return <SchedulingSetup onComplete={handleSetupComplete} />;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem' }}>Scheduling Dashboard</h1>
          <p style={{ margin: '5px 0 0', color: '#666' }}>
            Your booking page:{' '}
            <a href={profile?.bookingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
              {profile?.bookingUrl}
            </a>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isStaffSomewhere && (
            <Link
              to="/scheduling/staff"
              style={{
                padding: '10px 20px',
                backgroundColor: '#f0fdf4',
                color: '#166534',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              My Staff View
            </Link>
          )}
          <button
            onClick={() => setShowNewEventModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            + New Event Type
          </button>
          <Link
            to="/scheduling/settings"
            style={{
              padding: '10px 20px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            Settings
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '30px', borderBottom: '1px solid #e5e7eb', paddingBottom: '0' }}>
        {[
          { id: 'dashboard' as TabType, label: 'Dashboard' },
          { id: 'calendar' as TabType, label: 'Calendar' },
          { id: 'staff' as TabType, label: 'Staff' },
          { id: 'payments' as TabType, label: 'Payments' },
          { id: 'api' as TabType, label: 'API Keys' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
              color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
              border: activeTab === tab.id ? '1px solid #e5e7eb' : '1px solid transparent',
              borderBottom: activeTab === tab.id ? '1px solid white' : '1px solid transparent',
              borderRadius: '8px 8px 0 0',
              marginBottom: '-1px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '400',
              fontSize: '0.95rem'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'payments' && (
        <PaymentSettings plan={(profile?.plan as 'free' | 'pro') || 'free'} />
      )}

      {activeTab === 'api' && (
        <APIKeys plan={(profile?.plan as 'free' | 'pro') || 'free'} onUpgrade={() => navigate('/account')} />
      )}

      {activeTab === 'staff' && (
        <StaffManagement eventTypes={eventTypes} />
      )}

      {activeTab === 'calendar' && <SchedulingCalendarView />}

      {activeTab === 'dashboard' && (
      <>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, color: '#166534', fontSize: '0.875rem' }}>Upcoming</p>
          <p style={{ margin: '5px 0 0', fontSize: '2rem', fontWeight: '600', color: '#166534' }}>{stats?.upcomingBookings || 0}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, color: '#1e40af', fontSize: '0.875rem' }}>This Week</p>
          <p style={{ margin: '5px 0 0', fontSize: '2rem', fontWeight: '600', color: '#1e40af' }}>{stats?.bookingsThisWeek || 0}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#faf5ff', borderRadius: '12px', border: '1px solid #e9d5ff' }}>
          <p style={{ margin: 0, color: '#6b21a8', fontSize: '0.875rem' }}>This Month</p>
          <p style={{ margin: '5px 0 0', fontSize: '2rem', fontWeight: '600', color: '#6b21a8' }}>{stats?.bookingsThisMonth || 0}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fefce8', borderRadius: '12px', border: '1px solid #fef08a' }}>
          <p style={{ margin: 0, color: '#854d0e', fontSize: '0.875rem' }}>Total</p>
          <p style={{ margin: '5px 0 0', fontSize: '2rem', fontWeight: '600', color: '#854d0e' }}>{stats?.totalBookings || 0}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
        {/* Event Types */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '1.25rem' }}>Event Types</h2>

          {eventTypes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p>No event types yet.</p>
              <button
                onClick={() => setShowNewEventModal(true)}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Create your first event type
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {eventTypes.map(et => (
                <div
                  key={et.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${et.color || '#3b82f6'}`
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: '500' }}>{et.title}</p>
                    <p style={{ margin: '5px 0 0', fontSize: '0.875rem', color: '#666' }}>
                      {et.durationMinutes} min • {et.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Link
                      to={`/scheduling/event-types/${et.id}`}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        color: '#374151',
                        fontSize: '0.875rem'
                      }}
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => navigator.clipboard.writeText(`${profile?.bookingUrl}/${et.slug}`)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => handleDeleteEventType(et.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: '#dc2626',
                        fontSize: '0.875rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Bookings */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Upcoming Bookings</h2>
            <Link to="/scheduling/bookings" style={{ color: '#3b82f6', fontSize: '0.875rem' }}>
              View all
            </Link>
          </div>

          {upcomingBookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p>No upcoming bookings.</p>
              <p style={{ fontSize: '0.875rem' }}>Share your booking link to start receiving appointments.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {upcomingBookings.map(booking => (
                <div
                  key={booking.id}
                  style={{
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '500' }}>{booking.inviteeName}</p>
                      <p style={{ margin: '5px 0 0', fontSize: '0.875rem', color: '#666' }}>
                        {booking.inviteeEmail}
                      </p>
                    </div>
                    <span
                      style={{
                        padding: '4px 8px',
                        backgroundColor: booking.status === 'confirmed' ? '#dcfce7' : '#fef3c7',
                        color: booking.status === 'confirmed' ? '#166534' : '#854d0e',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}
                    >
                      {booking.status}
                    </span>
                  </div>
                  <p style={{ margin: '10px 0 0', fontSize: '0.875rem', color: '#374151' }}>
                    {formatDate(booking.startTime)}
                  </p>
                  {booking.meetLink && (
                    <a
                      href={booking.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        marginTop: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        textDecoration: 'none'
                      }}
                    >
                      Join Meeting
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Calendar Integration Status */}
      <div style={{ marginTop: '30px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: '1.25rem' }}>Calendar Integration</h2>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1, padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: calendarStatus?.google.connected ? '#dcfce7' : '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1.25rem' }}>📅</span>
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: '500' }}>Google Calendar</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.875rem', color: calendarStatus?.google.connected ? '#166534' : '#666' }}>
                  {calendarStatus?.google.connected ? 'Connected' : 'Not connected'}
                </p>
              </div>
            </div>
          </div>
          <div style={{ flex: 1, padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: calendarStatus?.microsoft.connected ? '#dcfce7' : '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1.25rem' }}>📆</span>
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: '500' }}>Microsoft Outlook</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.875rem', color: calendarStatus?.microsoft.connected ? '#166534' : '#666' }}>
                  {calendarStatus?.microsoft.connected ? 'Connected' : 'Not connected'}
                </p>
              </div>
            </div>
          </div>
        </div>
        <Link
          to="/scheduling/settings"
          style={{
            display: 'inline-block',
            marginTop: '15px',
            color: '#3b82f6',
            fontSize: '0.875rem'
          }}
        >
          Manage calendar connections →
        </Link>
      </div>
      </>
      )}

      {/* New Event Type Modal */}
      {showNewEventModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowNewEventModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px' }}>New Event Type</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Title</label>
              <input
                type="text"
                value={newEventTitle}
                onChange={e => setNewEventTitle(e.target.value)}
                placeholder="e.g., 30-Minute Meeting"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Duration</label>
              <select
                value={newEventDuration}
                onChange={e => setNewEventDuration(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>2 hours</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNewEventModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEventType}
                disabled={!newEventTitle.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: newEventTitle.trim() ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: newEventTitle.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulingDashboard;
